import { X, Check } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { useState } from "react";

const cards = [
  {
    frontTitle: "Who's Getting Your Calls?",
    frontBody: "Run ads and send you whoever clicks",
    backTitle: "Only Qualified Leads Reach You",
    backBody:
      "Every lead is filtered and qualified before it ever reaches your calendar — no time wasters, no tire kickers",
  },
  {
    frontTitle: "Built for Everyone. Optimized for No One.",
    frontBody: "Build campaigns for any type of business",
    backTitle: "Built Exclusively for Immigration Firms",
    backBody:
      "Every system, every ad, every automation is built around how immigration practices actually run — H-1B, green card, DACA, and everything in between",
  },
  {
    frontTitle: "Leads Dropped at the Door",
    frontBody: "Hand you leads and call it done",
    backTitle: "First Click to Signed Client",
    backBody:
      "We build the full pipeline — ads, follow-up, qualification, booking, and reminders — so leads become retained clients, not just names in a spreadsheet",
  },
  {
    frontTitle: "More Leads. More Chaos.",
    frontBody: "Flood your inbox and overwhelm your team",
    backTitle: "Growth at a Pace You Control",
    backBody:
      "The intake system handles volume automatically — your calendar fills with serious prospects while your team stays focused on casework",
  },
  {
    frontTitle: "Reports That Mean Nothing",
    frontBody: "Send monthly reports full of numbers that have nothing to do with your revenue",
    backTitle: "Numbers That Actually Matter",
    backBody:
      "Every report ties directly to consultations booked and clients signed — the only metrics that tell you if the system is working",
  },
];

function Flashcard({ card, index, reduced }: { card: typeof cards[number]; index: number; reduced: boolean | null }) {
  const [flipped, setFlipped] = useState(false);
  const [hasFlipped, setHasFlipped] = useState(false);

  const handleFlip = () => {
    setFlipped((f) => !f);
    if (!hasFlipped) setHasFlipped(true);
  };

  return (
    <motion.div
      initial={reduced ? false : { opacity: 0, scale: 0.95 }}
      whileInView={reduced ? undefined : { opacity: 1, scale: 1 }}
      viewport={{ once: true }}
      transition={reduced ? undefined : { delay: index * 0.1, duration: 0.4, ease: "easeOut" }}
      className="cursor-pointer w-full md:w-[280px] h-[180px] md:h-[200px] mx-auto"
      style={{ perspective: "1000px" }}
      onClick={handleFlip}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleFlip();
        }
      }}
      aria-label={flipped ? card.backTitle : card.frontTitle}
    >
      {reduced ? (
        <div className="relative w-full h-full">
          <motion.div
            animate={{ opacity: flipped ? 0 : 1 }}
            transition={{ duration: 0.1 }}
            className="absolute inset-0"
          >
            <FrontFace card={card} hasFlipped={hasFlipped} />
          </motion.div>
          <motion.div
            animate={{ opacity: flipped ? 1 : 0 }}
            transition={{ duration: 0.1 }}
            className="absolute inset-0"
          >
            <BackFace card={card} />
          </motion.div>
        </div>
      ) : (
        <motion.div
          className="relative w-full h-full"
          style={{ transformStyle: "preserve-3d" }}
          animate={{ rotateY: flipped ? 180 : 0 }}
          transition={{ duration: 0.5, ease: "easeInOut" }}
        >
          <div
            className="absolute inset-0"
            style={{ backfaceVisibility: "hidden", WebkitBackfaceVisibility: "hidden" }}
          >
            <FrontFace card={card} hasFlipped={hasFlipped} />
          </div>
          <div
            className="absolute inset-0"
            style={{
              backfaceVisibility: "hidden",
              WebkitBackfaceVisibility: "hidden",
              transform: "rotateY(180deg)",
            }}
          >
            <BackFace card={card} />
          </div>
        </motion.div>
      )}
    </motion.div>
  );
}

function FrontFace({ card, hasFlipped }: { card: typeof cards[number]; hasFlipped: boolean }) {
  return (
    <div className="relative w-full h-full rounded-xl border border-border bg-muted p-4 flex flex-col items-center justify-center text-center">
      <span className="absolute top-2 left-3 text-[10px] uppercase tracking-wider text-muted-foreground">
        Other Agencies
      </span>
      <X className="w-6 h-6 text-muted-foreground mb-2" />
      <h3 className="text-sm font-bold text-muted-foreground mb-1 px-2">{card.frontTitle}</h3>
      <p className="text-xs text-muted-foreground px-2">{card.frontBody}</p>
      {!hasFlipped && (
        <span className="absolute bottom-2 right-3 text-[10px] text-muted-foreground/60">Flip →</span>
      )}
    </div>
  );
}

function BackFace({ card }: { card: typeof cards[number] }) {
  return (
    <div
      className="relative w-full h-full rounded-xl border-2 border-primary bg-primary/10 p-4 flex flex-col items-center justify-center text-center"
      style={{ boxShadow: "0 0 20px hsl(var(--primary) / 0.15)" }}
    >
      <span className="absolute top-2 left-3 text-[10px] uppercase tracking-wider font-bold text-primary">
        FuzionLabs
      </span>
      <Check className="w-6 h-6 text-primary mb-2" />
      <h3 className="text-sm font-bold text-foreground mb-1 px-2">{card.backTitle}</h3>
      <p className="text-xs text-foreground/80 px-2">{card.backBody}</p>
    </div>
  );
}

export function Comparison() {
  const reduced = useReducedMotion();

  return (
    <section id="why-fuzionlabs" className="py-8 md:py-12 relative overflow-hidden">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-foreground text-center mb-3 tracking-tight">
          Most agencies run ads. We build systems.
        </h2>
        <p className="text-center text-sm md:text-base text-muted-foreground mb-10">
          Tap or click each card to flip it and see the difference.
        </p>

        <div className="flex flex-col items-center gap-4 md:gap-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 md:gap-6 w-full justify-items-center">
            {cards.slice(0, 3).map((card, i) => (
              <Flashcard key={i} card={card} index={i} reduced={reduced} />
            ))}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6 w-full sm:w-auto justify-items-center">
            {cards.slice(3).map((card, i) => (
              <Flashcard key={i + 3} card={card} index={i + 3} reduced={reduced} />
            ))}
          </div>
        </div>

        <motion.p
          initial={reduced ? false : { opacity: 0 }}
          whileInView={reduced ? undefined : { opacity: 1 }}
          viewport={{ once: true }}
          transition={reduced ? undefined : { delay: 0.6 }}
          className="text-center mt-10 text-lg text-muted-foreground"
        >
          You focus on the cases. We make sure there are always more coming.
        </motion.p>
      </div>
    </section>
  );
}
